import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Bell, X, Share, Smartphone, Check, MoreVertical } from 'lucide-react';
import { toast } from 'sonner';
import { pushSupported, pushGranted, enablePush } from '../utils/push';

const SNOOZE_KEY = 'engage-prompt-snooze';
const SNOOZE_DAYS = 3;

const InstallPrompt = () => {
  const { API, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSHelp, setShowIOSHelp] = useState(false);
  const [notifOn, setNotifOn] = useState(false);
  const [installing, setInstalling] = useState(false);

  const standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
  const notifNeeded = pushSupported() && Notification.permission === 'default';

  useEffect(() => {
    const snoozedAt = Number(localStorage.getItem(SNOOZE_KEY) || 0);
    if (snoozedAt && Date.now() - snoozedAt < SNOOZE_DAYS * 24 * 60 * 60 * 1000) return undefined;
    if (standalone && !notifNeeded) return undefined;

    const ua = window.navigator.userAgent || '';
    const ios = /iphone|ipad|ipod/i.test(ua) && !window.MSStream;
    setIsIOS(ios);

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    const t = setTimeout(() => setShow(true), 2500);
    return () => {
      clearTimeout(t);
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  const dismiss = () => {
    setShow(false);
    localStorage.setItem(SNOOZE_KEY, String(Date.now()));
  };

  const handleNotifications = async () => {
    if (!user) {
      toast.info('כדי לקבל התראות יש להתחבר קודם 🎾');
      dismiss();
      navigate('/auth');
      return;
    }
    try {
      await enablePush(API);
      setNotifOn(true);
      toast.success('התראות הופעלו בהצלחה! 🔔');
    } catch (e) {
      if (e.message === 'denied') toast.error('ההרשאה נדחתה - ניתן להפעיל דרך הגדרות הדפדפן');
      else toast.error('לא ניתן להפעיל התראות במכשיר זה');
    }
  };

  const handleInstall = async () => {
    if (deferred) {
      setInstalling(true);
      deferred.prompt();
      await deferred.userChoice;
      setDeferred(null);
      setInstalling(false);
      return;
    }
    setShowIOSHelp(true);
  };

  if (!show) return null;

  const showNotifRow = notifNeeded && !notifOn;
  const showInstallRow = !standalone;
  if (!showNotifRow && !showInstallRow && !notifOn) return null;

  return (
    <div
      className="fixed bottom-4 inset-x-3 sm:inset-x-auto sm:right-4 sm:max-w-sm z-[60] rounded-3xl shadow-2xl overflow-hidden brand-font animate-in slide-in-from-bottom duration-500"
      style={{ background: 'linear-gradient(160deg, #1e2b5e 0%, #17224d 55%, #0e1c3d 100%)' }}
      dir="rtl"
      data-testid="engagement-prompt"
    >
      <div className="absolute -top-10 -right-10 w-36 h-36 rounded-full blur-3xl pointer-events-none" style={{ background: 'rgba(29,111,242,0.35)' }} />
      <div className="absolute -bottom-12 -left-10 w-36 h-36 rounded-full blur-3xl pointer-events-none" style={{ background: 'rgba(16,180,126,0.28)' }} />

      <div className="relative p-4">
        {/* Header */}
        <div className="flex items-center gap-3 mb-3">
          <div className="w-11 h-11 rounded-2xl bg-white p-1 shadow-lg shrink-0">
            <img src="/assets/tennis-pro-logo.jpg" alt="Tennis Pro" className="w-full h-full object-contain rounded-xl" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-white text-sm">אל תפספסו אף משחק! 🎾</div>
            <div className="text-[11px] text-white/70">שדרגו את החוויה שלכם באתר</div>
          </div>
          <button onClick={dismiss} data-testid="engagement-prompt-dismiss" className="shrink-0 text-white/60 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-2">
          {/* Notifications row */}
          {(showNotifRow || notifOn) && (
            <button
              onClick={notifOn ? undefined : handleNotifications}
              data-testid="engagement-enable-notifications"
              className={`w-full flex items-center gap-3 rounded-2xl p-3 text-right transition-all ${
                notifOn
                  ? 'bg-[#10b47e]/20 border border-[#10b47e]/40'
                  : 'bg-white/10 border border-white/15 hover:bg-white/15 active:scale-[0.98]'
              }`}
            >
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${notifOn ? 'bg-[#10b47e]' : 'bg-gradient-to-br from-[#1d6ff2] to-cyan-400'}`}>
                {notifOn ? <Check className="w-5 h-5 text-white" /> : <Bell className="w-5 h-5 text-white" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-white font-bold text-sm">{notifOn ? 'התראות פעילות ✓' : 'הפעלת התראות'}</div>
                <div className="text-white/65 text-[11px]">עדכונים על תחרויות, תוצאות והודעות</div>
              </div>
            </button>
          )}

          {/* Install row */}
          {showInstallRow && (
            <button
              onClick={handleInstall}
              disabled={installing}
              data-testid="engagement-install-app"
              className="w-full flex items-center gap-3 rounded-2xl p-3 text-right bg-white/10 border border-white/15 hover:bg-white/15 active:scale-[0.98] transition-all"
            >
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#10b47e] to-emerald-400 flex items-center justify-center shrink-0">
                <Smartphone className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-white font-bold text-sm">שמירה למסך הבית</div>
                <div className="text-white/65 text-[11px]">גישה מהירה כמו אפליקציה, בלי חנות</div>
              </div>
            </button>
          )}

          {/* iOS / manual install instructions */}
          {showIOSHelp && (
            <div className="rounded-2xl bg-white/10 border border-white/15 p-3 text-white/85 text-xs leading-relaxed" data-testid="engagement-install-help">
              {isIOS ? (
                <span className="flex items-center gap-1 flex-wrap">
                  לחצו על <Share className="w-4 h-4 inline text-cyan-300" /> <b>שיתוף</b> בסרגל הדפדפן ← ואז <b>"הוסף למסך הבית"</b>
                </span>
              ) : (
                <span className="flex items-center gap-1 flex-wrap">
                  פתחו את תפריט הדפדפן <MoreVertical className="w-4 h-4 inline text-cyan-300" /> ← ובחרו <b>"הוספה למסך הבית"</b> / <b>"התקנת אפליקציה"</b>
                </span>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="h-1 bg-gradient-to-r from-[#1d6ff2] via-cyan-400 to-[#10b47e]" />
    </div>
  );
};

export default InstallPrompt;
